import React, { useEffect, useMemo, useState } from "react";
import { DA_Button } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import { DA_Container } from "@danishagro/shared/src/components/atoms/Container/Container.component";
import { DA_Icon, DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import { DA_InlineLink } from "@danishagro/shared/src/components/atoms/InlineLink/InlineLink.component";
import { DA_Title } from "@danishagro/shared/src/components/atoms/Title/Title.component";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { formatNumber } from "@danishagro/shared/src/helpers/formatNumber.helper";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import {
    DA_EmptyState,
    DA_EmptyStateTypes,
} from "@danishagro/shared/src/components/molecules/EmptyState/EmptyState.component";
import { DA_ContentBox } from "@danishagro/shared/src/components/atoms/ContentBox/ContentBox.component";
import { DA_Text } from "@danishagro/shared/src/components/atoms/Text/Text.component";
import { useScreen } from "@danishagro/shared/src/contexts/screen.context";
import { format } from "date-fns";
import { useBasket } from "@danishagro/shared/src/contexts/basket/basket.context";
import Cookies from "js-cookie";
import { useCompletedOrder } from "./hooks/useCompletedOrder.hook";
import { DA_ReceiptDesktop } from "./components/desktop/ReceiptDesktop.component";
import { DA_ReceiptMobile } from "./components/mobile/ReceiptMobile.component";
import S from "./ReceiptV1.module.scss";

export interface V1Props {
    basketId: string;
    basketSecret: string;
}

export const V1 = ({ basketId, basketSecret }: V1Props) => {
    const { getDictionaryString } = useTranslations();
    const { currentCulture } = useAppData();
    const { isMobile, scrollTo } = useScreen();
    const { resetBasket } = useBasket();

    // Completed order
    const {
        awaitingResponse,
        error,
        orderLines,
        price,
        customerDetails,
        shippingDetails,
        invoiceDetails,
        completedDate,
        deliveryMethod,
        paymentMethod,
        comment,
        servicePoint,
    } = useCompletedOrder(basketId, basketSecret);

    const [customerNumber] = useState<string>("");

    // Reset basket on mount
    useEffect(() => {
        resetBasket();
        // Do not clear cookies until this point in case user cancels quickpay. To avoid losing user data in checkout form
        Cookies.remove("checkoutFormData");
    }, [resetBasket]);

    useEffect(() => {
        scrollTo(0);
    }, [scrollTo]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [customer] = useState<any>("");

    const priceFormatted = useMemo(
        () =>
            formatNumber(price?.totalPriceWithVat ?? 0, currentCulture, {
                decimals: 2,
            }),
        [price, currentCulture]
    );

    const completedDateFormatted = useMemo(() => {
        if (!completedDate) return "";

        try {
            return format(new Date(completedDate), "dd.MM.yyyy");
        } catch (err) {
            return "";
        }
    }, [completedDate]);

    return (
        <DA_Container className={S.wrapper} noTopPadding>
            {/** Header */}
            <div className={S.header}>
                <DA_Title h1 noMargin>
                    {getDictionaryString("ReceiptHeaderHeading")}
                </DA_Title>
                <DA_InlineLink
                    href="/"
                    forceNativeUrl={true}
                    title={getDictionaryString("ReceiptToFrontpage")}
                />
            </div>

            {/** Banner - Only show success banner when order is successfully loaded */}
            {!awaitingResponse && !error && orderLines?.length ? (
                <DA_ContentBox>
                    <div className={S.banner}>
                        <DA_Icon className={S.bannerIcon} name={DA_IconNames.CheckmarkThin} />

                        <div className={S.bannerContent}>
                            <DA_Title h3>{getDictionaryString("ReceiptBannerHeading")}</DA_Title>
                            <DA_Text
                                noPadding
                                html={getDictionaryString("ReceiptBannerRichText", {
                                    email: customerDetails?.email,
                                })}
                            />
                        </div>
                    </div>
                </DA_ContentBox>
            ) : null}

            {/** List */}
            {awaitingResponse ? (
                <DA_ContentBox>
                    <DA_EmptyState
                        title={getDictionaryString("ReceiptLoadingOrder")}
                        type={DA_EmptyStateTypes.Loading}
                        smallText
                        iconOnly
                    />
                </DA_ContentBox>
            ) : error ? (
                <DA_ContentBox>
                    <DA_EmptyState
                        title={getDictionaryString("ReceiptOrderError")}
                        text={error}
                        type={DA_EmptyStateTypes.Error}
                        smallText
                    />
                </DA_ContentBox>
            ) : !orderLines?.length ? (
                <DA_ContentBox>
                    <DA_EmptyState
                        title={getDictionaryString("ReceiptError")}
                        type={DA_EmptyStateTypes.Error}
                        smallText
                    />
                </DA_ContentBox>
            ) : isMobile ? (
                // TODO: Add content for DA_ReceiptMobile component
                <DA_ReceiptMobile
                    priceFormatted={priceFormatted}
                    customerDetails={customerDetails}
                    shippingDetails={shippingDetails}
                    customer={customer}
                    customerNumber={customerNumber}
                    userAccountDetails={customerDetails}
                    orderLines={orderLines}
                    price={price}
                    invoiceDetails={invoiceDetails}
                    deliveryMethod={deliveryMethod}
                    paymentMethod={paymentMethod}
                    comment={comment}
                    servicePoint={servicePoint}
                />
            ) : (
                <DA_ReceiptDesktop
                    priceFormatted={priceFormatted}
                    customerDetails={customerDetails}
                    shippingDetails={shippingDetails}
                    customer={customer}
                    customerNumber={customerNumber}
                    userAccountDetails={customerDetails}
                    orderLines={orderLines}
                    price={price}
                    completedDateFormatted={completedDateFormatted}
                    invoiceDetails={invoiceDetails}
                    deliveryMethod={deliveryMethod}
                    paymentMethod={paymentMethod}
                    comment={comment}
                    servicePoint={servicePoint}
                />
            )}

            {/** Toolbar */}
            <div className={S.buttonWrapper}>
                <DA_Button
                    forceNativeUrl
                    href="/"
                    title={getDictionaryString("ReceiptToFrontpage")}
                />
            </div>
        </DA_Container>
    );
};
